
import { Bounce, Slide, Sparkle, Wave } from 'kinetic-typos-jin';
import { useState, useEffect, useRef } from 'react';

function BounceText({ text, fontSize, speed, color1, color2, fontWeight, fontFamily }) {
  const ref = useRef();

  useEffect(() => {
    const instance = new Bounce(text, { fontSize, speed, color1, color2, fontWeight, fontFamily });
    const el = instance.render();
    ref.current.innerHTML = '';
    ref.current.appendChild(el);
  }, [text, fontSize, speed, color1, color2, fontWeight, fontFamily]);

  return <span ref={ref} />;
}

function SlideText({ text1, text2, fontSize, speed, color1, color2, fontWeight, fontFamily }) {
  const ref = useRef();

  useEffect(() => {
    const instance = new Slide(text1, text2, { fontSize, speed, color1, color2, fontWeight, fontFamily });
    const el = instance.render();
    ref.current.innerHTML = '';
    ref.current.appendChild(el);
  }, [text1, text2, fontSize, speed, color1, color2, fontWeight, fontFamily]);

  return <span ref={ref} />;
}


function SparkleText({ text, fontSize, color1, color2, fontWeight, fontFamily }) {
  const ref = useRef();

  useEffect(() => {
    const instance = new Sparkle(text, { fontSize, color1, color2, fontWeight, fontFamily });
    const el = instance.render();
    ref.current.innerHTML = '';
    ref.current.appendChild(el);
  }, [text, fontSize, color1, color2, fontWeight, fontFamily]);

  return <span ref={ref} />;
}

function WaveText({ text, fontSize, fontWeight, fontFamily }) {
  const ref = useRef();

  useEffect(() => {
    const instance = new Wave(text, { fontSize, fontWeight, fontFamily });
    const el = instance.render();
    ref.current.innerHTML = '';
    ref.current.appendChild(el);
  }, [text, fontSize, fontWeight, fontFamily]);

  return <span ref={ref} />;
}




export default function Yuki() {

  const [data, setData] = useState([]);
  const [syncStatus, setSyncStatus] = useState(null);
  const [isSyncing, setIsSyncing] = useState(false);
  const [cachedSeries, setCachedSeries] = useState([]);
  
  const [aboutSyncStatus, setAboutSyncStatus] = useState(null);
  const [isAboutSyncing, setIsAboutSyncing] = useState(false);
  const [cachedAbout, setCachedAbout] = useState(null);
  const [aboutRefreshStatus, setAboutRefreshStatus] = useState(null);
  const [isAboutRefreshing, setIsAboutRefreshing] = useState(false);
  const [blogSyncStatus, setBlogSyncStatus] = useState(null);
  const [isBlogSyncing, setIsBlogSyncing] = useState(false);

  const fetchData = async () => {        
    try {
      const response = await fetch(`https://imajin.dev/api/query-a-database-projects-home`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "ngrok-skip-browser-warning": "true",
          "User-Agent": "Mozilla/5.0"
        },
      });
  
      if (!response.ok) {
        throw new Error("Network response was not ok");
      }
  
      const result = await response.json();
        setData(result);

    } catch (error) {
      console.error("Error fetching data:", error.message);
    }
  };

  const fetchCachedSeries = async () => {
    try {
      const response = await fetch(`https://imajin.dev/api/query-a-database-series`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
      });

      if (!response.ok) {
        throw new Error("Failed to fetch cached series");
      }

      const result = await response.json();
      setCachedSeries(result);
    } catch (error) {
      console.error("Error fetching cached series:", error.message);
    }
  };

  const syncSeriesData = async () => {
    setIsSyncing(true);
    setSyncStatus(null);
    
    try {
      const endpoints = [
        "https://imajin.dev/api/sync-series-data",
        "/api/sync-series-data",
      ];

      let result = null;
      let lastError = null;

      for (const url of endpoints) {
        try {
          console.log("Syncing from URL:", url);

          const response = await fetch(url, {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
            },
          });

          if (!response.ok) {
            const errorText = await response.text();
            throw new Error(`HTTP ${response.status}: ${errorText || response.statusText}`);
          }

          result = await response.json();
          break;
        } catch (error) {
          lastError = error;
        }
      }

      if (!result) {
        throw lastError || new Error("All series sync endpoints failed");
      }

      setSyncStatus({
        success: true,
        message: result.message,
        count: result.count
      });
      
      // Fetch the newly cached data to display
      await fetchCachedSeries();
      
      console.log("Sync successful:", result);
    } catch (error) {
      setSyncStatus({
        success: false,
        message: error.message
      });
      console.error("Error syncing series data:", error.message);
    } finally {
      setIsSyncing(false);
    }
  };

  const fetchCachedAbout = async () => {
    try {
      const response = await fetch(`https://imajin.dev/api/retrieve-block-children-about`, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
        },
      });

      if (!response.ok) {
        throw new Error("Failed to fetch cached about data");
      }

      const result = await response.json();
      setCachedAbout(result);
    } catch (error) {
      console.error("Error fetching cached about data:", error.message);
    }
  };

  const syncAboutData = async () => {
    setIsAboutSyncing(true);
    setAboutSyncStatus(null);
    
    try {
      const url = `https://imajin.dev/api/sync-about-data`;
      console.log("Syncing about from URL:", url);
      
      const response = await fetch(url, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`HTTP ${response.status}: ${errorText || response.statusText}`);
      }

      const result = await response.json();
      setAboutSyncStatus({
        success: true,
        message: result.message,
        blockCount: result.blockCount
      });
      
      // Fetch the newly cached data to display
      await fetchCachedAbout();
      
      console.log("About sync successful:", result);
    } catch (error) {
      setAboutSyncStatus({
        success: false,
        message: error.message
      });
      console.error("Error syncing about data:", error.message);
    } finally {
      setIsAboutSyncing(false);
    }
  };

  const refreshAboutCache = async () => {
    setIsAboutRefreshing(true);
    setAboutRefreshStatus(null);

    try {
      const url = `https://imajin.dev/api/refresh-about-cache`;
      console.log("Refreshing about cache from URL:", url);

      const response = await fetch(url, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`HTTP ${response.status}: ${errorText || response.statusText}`);
      }

      const result = await response.json();
      setAboutRefreshStatus({
        success: true,
        message: result.message,
        blockCount: result.blockCount
      });

      setCachedAbout({ results: result.results || [] });
      console.log("About cache refresh successful:", result);
    } catch (error) {
      setAboutRefreshStatus({
        success: false,
        message: error.message
      });
      console.error("Error refreshing about cache:", error.message);
    } finally {
      setIsAboutRefreshing(false);
    }
  };

  const syncBlogCache = async () => {
    setIsBlogSyncing(true);
    setBlogSyncStatus(null);

    try {
      const endpoints = [
        "/api/sync-blog-cache",
        "/api/sync-blog-data",
        "https://imajin.dev/api/sync-blog-cache",
        "https://imajin.dev/api/sync-blog-data",
      ];

      let result = null;
      let lastError = null;

      for (const url of endpoints) {
        try {
          console.log("Syncing blog cache from URL:", url);

          const response = await fetch(url, {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
            },
          });

          if (!response.ok) {
            const errorText = await response.text();
            throw new Error(`HTTP ${response.status}: ${errorText || response.statusText}`);
          }

          result = await response.json();
          break;
        } catch (error) {
          lastError = error;
        }
      }

      if (!result) {
        throw lastError || new Error("All blog sync endpoints failed");
      }

      setBlogSyncStatus({
        success: true,
        message: result.message,
        count: result.count
      });

      console.log("Blog cache sync successful:", result);
    } catch (error) {
      setBlogSyncStatus({
        success: false,
        message: error.message
      });
      console.error("Error syncing blog cache:", error.message);
    } finally {
      setIsBlogSyncing(false);
    }
  };

  useEffect( () => {
    fetchData();
    fetchCachedSeries();
    fetchCachedAbout();
  }, []);

  return (
    <div className="page">
      <h1>API Test</h1>
      <p>This is the Yuki page.</p>

      <h2>Series Data Cache</h2>
      
      <div style={{ marginBottom: '20px' }}>
        <h3 style={{ marginBottom: '12px' }}>Available APIs:</h3>

        <div style={{ 
          backgroundColor: '#f5f5f5', 
          padding: '12px', 
          borderRadius: '4px',
          marginBottom: '12px',
          borderLeft: '4px solid #E91E63'
        }}>
          <strong>POST /api/sync-blog-cache</strong>
          <p style={{ margin: '8px 0 0 0', fontSize: '14px', color: '#666' }}>
            Syncs every live blog post from Notion into a local cache so BlogItem pages can load without hitting Notion on every request.
          </p>
        </div>

        <div style={{ marginBottom: '16px' }}>
          <button 
            onClick={syncBlogCache} 
            disabled={isBlogSyncing}
            style={{
              padding: '8px 16px',
              backgroundColor: isBlogSyncing ? '#ccc' : '#E91E63',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: isBlogSyncing ? 'not-allowed' : 'pointer',
              fontSize: '14px'
            }}
          >
            {isBlogSyncing ? 'Syncing...' : 'Sync Blog Cache'}
          </button>
        </div>

        {blogSyncStatus && (
          <div style={{
            padding: '12px',
            backgroundColor: blogSyncStatus.success ? '#d4edda' : '#f8d7da',
            color: blogSyncStatus.success ? '#155724' : '#721c24',
            borderRadius: '4px',
            border: `1px solid ${blogSyncStatus.success ? '#c3e6cb' : '#f5c6cb'}`,
            marginBottom: '16px'
          }}>
            <strong>{blogSyncStatus.success ? '✓ Success' : '✗ Error'}:</strong> {blogSyncStatus.message}
            {blogSyncStatus.count && ` (${blogSyncStatus.count} blog posts)`}
          </div>
        )}

        <div style={{ 
          backgroundColor: '#f5f5f5', 
          padding: '12px', 
          borderRadius: '4px',
          marginBottom: '12px',
          borderLeft: '4px solid #00ACC1'
        }}>
          <strong>POST /api/refresh-about-cache</strong>
          <p style={{ margin: '8px 0 0 0', fontSize: '14px', color: '#666' }}>
            Refreshes the cached About blocks so file-backed image and video URLs stay fresh when the page opens.
          </p>
        </div>
        
        <div style={{ 
          backgroundColor: '#f5f5f5', 
          padding: '12px', 
          borderRadius: '4px',
          marginBottom: '12px',
          borderLeft: '4px solid #4CAF50'
        }}>
          <strong>POST /api/sync-series-data</strong>
          <p style={{ margin: '8px 0 0 0', fontSize: '14px', color: '#666' }}>
            Fetches fresh series data from Notion and updates the local cache file. Press the button below to trigger this sync after updating your series in Notion.
          </p>
        </div>

        <div style={{ 
          backgroundColor: '#f5f5f5', 
          padding: '12px', 
          borderRadius: '4px',
          borderLeft: '4px solid #2196F3'
        }}>
          <strong>POST /api/query-a-database-series</strong>
          <p style={{ margin: '8px 0 0 0', fontSize: '14px', color: '#666' }}>
            Serves cached series data from local file. This is what the Series page calls to load data. Fast response with no Notion API delay.
          </p>
        </div>
      </div>

      <div style={{ marginBottom: '16px' }}>
        <button 
          onClick={syncSeriesData} 
          disabled={isSyncing}
          style={{
            padding: '8px 16px',
            backgroundColor: isSyncing ? '#ccc' : '#4CAF50',
            color: 'white',
            border: 'none',
            borderRadius: '4px',
            cursor: isSyncing ? 'not-allowed' : 'pointer',
            fontSize: '14px'
          }}
        >
          {isSyncing ? 'Syncing...' : 'Sync Series Data'}
        </button>
      </div>
      
      {syncStatus && (
        <div style={{
          padding: '12px',
          backgroundColor: syncStatus.success ? '#d4edda' : '#f8d7da',
          color: syncStatus.success ? '#155724' : '#721c24',
          borderRadius: '4px',
          border: `1px solid ${syncStatus.success ? '#c3e6cb' : '#f5c6cb'}`
        }}>
          <strong>{syncStatus.success ? '✓ Success' : '✗ Error'}:</strong> {syncStatus.message}
          {syncStatus.count && ` (${syncStatus.count} series)`}
        </div>
      )}

      {cachedSeries.length > 0 && (
        <div style={{ marginTop: '20px', marginBottom: '20px' }}>
          <h3 style={{ marginBottom: '12px' }}>Cached Series ({cachedSeries.length}):</h3>
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(250px, 1fr))',
            gap: '12px'
          }}>
            {cachedSeries.map((series) => (
              <div
                key={series.id}
                style={{
                  padding: '12px',
                  backgroundColor: '#f9f9f9',
                  border: '1px solid #e0e0e0',
                  borderRadius: '4px',
                  boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
                }}
              >
                <div style={{ fontWeight: 'bold', marginBottom: '4px', wordBreak: 'break-word' }}>
                  {series.properties?.Name?.title?.[0]?.plain_text || 'Untitled'}
                </div>
                <div style={{ fontSize: '12px', color: '#999', wordBreak: 'break-all' }}>
                  ID: {series.id.substring(0, 8)}...
                </div>
                {series.properties?.Summary?.rich_text?.[0]?.plain_text && (
                  <div style={{ fontSize: '12px', color: '#666', marginTop: '6px' }}>
                    {series.properties.Summary.rich_text[0].plain_text}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      <h2 style={{ marginTop: '32px' }}>About Page Cache</h2>
      
      <div style={{ marginBottom: '20px' }}>
        <h3 style={{ marginBottom: '12px' }}>Available APIs:</h3>
        
        <div style={{ 
          backgroundColor: '#f5f5f5', 
          padding: '12px', 
          borderRadius: '4px',
          marginBottom: '12px',
          borderLeft: '4px solid #FF9800'
        }}>
          <strong>POST /api/sync-about-data</strong>
          <p style={{ margin: '8px 0 0 0', fontSize: '14px', color: '#666' }}>
            Fetches fresh About page blocks from Notion and updates the local cache file. Press the button below to sync after updating your About page.
          </p>
        </div>

        <div style={{ 
          backgroundColor: '#f5f5f5', 
          padding: '12px', 
          borderRadius: '4px',
          borderLeft: '4px solid #9C27B0'
        }}>
          <strong>GET /api/retrieve-block-children-about</strong>
          <p style={{ margin: '8px 0 0 0', fontSize: '14px', color: '#666' }}>
            Serves cached About page blocks from local file. Fast response with no Notion API delay.
          </p>
        </div>
      </div>

      <div style={{ marginBottom: '16px' }}>
        <button 
          onClick={refreshAboutCache} 
          disabled={isAboutRefreshing}
          style={{
            padding: '8px 16px',
            backgroundColor: isAboutRefreshing ? '#ccc' : '#00ACC1',
            color: 'white',
            border: 'none',
            borderRadius: '4px',
            cursor: isAboutRefreshing ? 'not-allowed' : 'pointer',
            fontSize: '14px',
            marginRight: '8px'
          }}
        >
          {isAboutRefreshing ? 'Refreshing...' : 'Refresh About Cache'}
        </button>
      </div>

      {aboutRefreshStatus && (
        <div style={{
          padding: '12px',
          backgroundColor: aboutRefreshStatus.success ? '#d4edda' : '#f8d7da',
          color: aboutRefreshStatus.success ? '#155724' : '#721c24',
          borderRadius: '4px',
          border: `1px solid ${aboutRefreshStatus.success ? '#c3e6cb' : '#f5c6cb'}`,
          marginBottom: '16px'
        }}>
          <strong>{aboutRefreshStatus.success ? '✓ Success' : '✗ Error'}:</strong> {aboutRefreshStatus.message}
          {aboutRefreshStatus.blockCount && ` (${aboutRefreshStatus.blockCount} blocks)`}
        </div>
      )}

      <div style={{ marginBottom: '16px' }}>
        <button 
          onClick={syncAboutData} 
          disabled={isAboutSyncing}
          style={{
            padding: '8px 16px',
            backgroundColor: isAboutSyncing ? '#ccc' : '#FF9800',
            color: 'white',
            border: 'none',
            borderRadius: '4px',
            cursor: isAboutSyncing ? 'not-allowed' : 'pointer',
            fontSize: '14px'
          }}
        >
          {isAboutSyncing ? 'Syncing...' : 'Sync About Data'}
        </button>
      </div>
      
      {aboutSyncStatus && (
        <div style={{
          padding: '12px',
          backgroundColor: aboutSyncStatus.success ? '#d4edda' : '#f8d7da',
          color: aboutSyncStatus.success ? '#155724' : '#721c24',
          borderRadius: '4px',
          border: `1px solid ${aboutSyncStatus.success ? '#c3e6cb' : '#f5c6cb'}`,
          marginBottom: '16px'
        }}>
          <strong>{aboutSyncStatus.success ? '✓ Success' : '✗ Error'}:</strong> {aboutSyncStatus.message}
          {aboutSyncStatus.blockCount && ` (${aboutSyncStatus.blockCount} blocks)`}
        </div>
      )}

      {cachedAbout && cachedAbout.results && cachedAbout.results.length > 0 && (
        <div style={{ marginTop: '20px', marginBottom: '20px' }}>
          <h3 style={{ marginBottom: '12px' }}>Cached About Blocks ({cachedAbout.results.length}):</h3>
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(250px, 1fr))',
            gap: '12px'
          }}>
            {cachedAbout.results.slice(0, 5).map((block, index) => (
              <div
                key={block.id}
                style={{
                  padding: '12px',
                  backgroundColor: '#f9f9f9',
                  border: '1px solid #e0e0e0',
                  borderRadius: '4px',
                  boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
                }}
              >
                <div style={{ fontWeight: 'bold', marginBottom: '4px' }}>
                  Block {index + 1} - {block.type}
                </div>
                <div style={{ fontSize: '12px', color: '#999' }}>
                  ID: {block.id.substring(0, 8)}...
                </div>
                {block.paragraph && block.paragraph.rich_text && block.paragraph.rich_text.length > 0 && (
                  <div style={{ fontSize: '12px', color: '#666', marginTop: '6px' }}>
                    {block.paragraph.rich_text[0].plain_text.substring(0, 80)}...
                  </div>
                )}
              </div>
            ))}
          </div>
          {cachedAbout.results.length > 5 && (
            <p style={{ fontSize: '12px', color: '#999', marginTop: '8px' }}>
              ...and {cachedAbout.results.length - 5} more blocks
            </p>
          )}
        </div>
      )}

      <hr/>
      <h2>Home</h2>
      <button>Copy Reformatted Data</button>

      <h2>kinetic-typos-jin</h2>
      <div>
        <BounceText text="Hello World" fontSize={32} color1="white" color2="#f5b326" />
        <SlideText text1="World " text2="World" fontSize={24} color1 color2="#AF50FF" />
        <SparkleText text="Hello World" fontSize={28} />
        <WaveText text="Hello World" fontSize={24} />
      </div>
    </div>
  );
}